import { useRef, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import axios from "axios";
import { ArrowRight, ImageIcon, Heart, Crown, Waves, MessageCircle, Plane } from "lucide-react";
import { Navbar } from "@/components/Navbar";
import { Footer } from "@/components/Footer";
import { Marquee } from "@/components/Marquee";
import { OffreDecouverteBanner } from "@/components/OffreDecouverteBanner";
import { AnnoncesALaUne } from "@/components/AnnoncesALaUne";
import { VisitorCounter } from "@/components/VisitorCounter";
import { CATEGORIES } from "@/lib/categories";
import { AIR_BENGUE } from "@/lib/airBengue";

// Visuel fourni par le propriétaire pour la carte 04 « Escale en Europe ».
const IMAGE_EUROPE =
  "https://customer-assets-7cd3h4nn.emergentagent.net/job_fodindin-preview-1/artifacts/0e9c90f470c3ec59_clean0.jpg";
import { useSeo, useDonneesStructurees } from "@/lib/seo";

const catIcons = { lago: Waves, love: Heart, vip: Crown };

const CategoryCard = ({ cat, index }) => {
  const Icon = catIcons[cat.id];
  return (
    <motion.div
      initial={{ opacity: 0, y: 40 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-60px" }}
      transition={{ duration: 0.6, delay: index * 0.12 }}
    >
      <Link
        to={`/categorie/${cat.id}/choix`}
        data-testid={`category-card-fodindin${cat.id === "vip" ? "clubvip" : cat.id === "lago" ? "lago" : "love"}`}
        className="group block h-full bg-white rounded-3xl border border-[#E5DFD5] overflow-hidden hover:shadow-2xl hover:-translate-y-2 transition-all duration-500"
      >
        <div
          className="relative h-72 m-4 rounded-2xl overflow-hidden"
          style={{ backgroundColor: cat.colorSoft }}
        >
          {cat.image ? (
            <>
              <img
                src={cat.image}
                alt={cat.name}
                className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-700"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/40 via-transparent to-transparent" />
              <span
                className="absolute bottom-3 right-4 font-heading text-5xl font-extrabold text-white/70 drop-shadow-lg"
              >
                {cat.number}
              </span>
              <Icon size={26} strokeWidth={1.6} className="absolute top-4 right-4 text-white drop-shadow-md" />
            </>
          ) : (
            <div className="h-full flex flex-col items-center justify-center gap-3 border-2 border-dashed rounded-2xl" style={{ borderColor: `${cat.color}55` }}>
              <Icon size={44} strokeWidth={1.4} style={{ color: cat.color }} />
              <div className="flex items-center gap-2 text-xs font-medium text-stone-500">
                <ImageIcon size={14} />
                <span>Emplacement image — à venir</span>
              </div>
            </div>
          )}
        </div>
        <div className="px-7 pb-7 pt-2 space-y-4">
          <span
            className="inline-block text-[11px] font-bold tracking-[0.15em] uppercase px-3 py-1.5 rounded-full border"
            style={{ color: cat.color, borderColor: `${cat.color}40`, backgroundColor: cat.colorSoft }}
          >
            {cat.badge}
          </span>
          <h3 className="font-heading text-2xl font-extrabold text-stone-900">{cat.name}</h3>
          <p className="text-sm text-stone-600 leading-relaxed">{cat.description}</p>
          <div className="flex items-center justify-between pt-2">
            <span className="text-xs font-semibold text-stone-500">{cat.pricing}</span>
            <span
              className="inline-flex items-center gap-1.5 text-sm font-bold group-hover:gap-3 transition-all duration-300"
              style={{ color: cat.color }}
            >
              Voir les profils <ArrowRight size={16} />
            </span>
          </div>
        </div>
      </Link>
    </motion.div>
  );
};

/** Carte 04 « Europe » : demandée par les visiteurs arrivant en Europe.
 *  Elle renvoie vers FodindinAir-Bengue (prestations hors Côte d'Ivoire). */
const CarteEurope = ({ index }) => (
  <motion.div
    initial={{ opacity: 0, y: 40 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true, margin: "-60px" }}
    transition={{ duration: 0.6, delay: index * 0.12 }}
  >
    <Link
      to={AIR_BENGUE.route}
      data-testid="category-card-europe"
      className="group block h-full bg-white rounded-3xl border border-[#E5DFD5] overflow-hidden hover:shadow-2xl hover:-translate-y-2 transition-all duration-500"
    >
      <div className="relative h-72 m-4 rounded-2xl overflow-hidden" style={{ backgroundColor: AIR_BENGUE.colorSoft }}>
        <img
          src={IMAGE_EUROPE}
          alt="Parenthèse de plaisir et de détente en Europe"
          data-testid="category-card-europe-image"
          className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-700"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/40 via-transparent to-transparent" />
        <span className="absolute bottom-3 right-4 font-heading text-5xl font-extrabold text-white/70 drop-shadow-lg">
          04
        </span>
        <Plane size={26} strokeWidth={1.6} className="absolute top-4 right-4 -rotate-45 text-white drop-shadow-md" />
      </div>
      <div className="px-7 pb-7 pt-2 space-y-4">
        <span
          className="inline-block text-[11px] font-bold tracking-[0.15em] uppercase px-3 py-1.5 rounded-full border"
          style={{ color: AIR_BENGUE.color, borderColor: `${AIR_BENGUE.color}40`, backgroundColor: AIR_BENGUE.colorSoft }}
        >
          Nouveau en Europe
        </span>
        <h3 className="font-heading text-2xl font-extrabold text-stone-900">Escale en Europe</h3>
        <p className="text-sm text-stone-600 leading-relaxed" data-testid="category-card-europe-texte">
          Vous venez d'arriver en Europe ? Offrez-vous aussi une parenthèse de plaisir et de
          détente. De Paris à Bruxelles, en passant par Londres, prenez le temps de savourer
          chaque instant. Une expérience chaleureuse, élégante et pleine de belles surprises
          pour rendre votre séjour vraiment inoubliable.
        </p>
        <div className="flex items-center justify-between pt-2">
          <span className="text-xs font-semibold text-stone-500">Paris · Bruxelles · Londres</span>
          <span
            className="inline-flex items-center gap-1.5 text-sm font-bold group-hover:gap-3 transition-all duration-300"
            style={{ color: AIR_BENGUE.color }}
          >
            Voir les profils <ArrowRight size={16} />
          </span>
        </div>
      </div>
    </Link>
  </motion.div>
);

export default function Home() {
  const heroRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: heroRef, offset: ["start start", "end start"] });
  const yOrb = useTransform(scrollYProgress, [0, 1], [0, 180]);
  const yText = useTransform(scrollYProgress, [0, 1], [0, 90]);
  const [visites, setVisites] = useState({});

  useSeo({
    titre: "Rencontres en Côte d'Ivoire — annonces Lago, Love, ClubVIP et Air-Bengue",
    description:
      "FodindinCa : annonces de rencontres à Abidjan et partout en Côte d'Ivoire. " +
      "Lago, Love, ClubVIP et Air-Bengue — photos en clair, contact direct dès l'abonnement.",
    chemin: "/",
  });
  useDonneesStructurees({
    "@context": "https://schema.org",
    "@type": "WebSite",
    name: "FodindinCa",
    url: "https://fodindinca.com/",
    inLanguage: "fr-CI",
    description:
      "Annonces de rencontres en Côte d'Ivoire : Lago, Love, ClubVIP et Air-Bengue.",
    publisher: { "@type": "Organization", name: "FodindinCa", url: "https://fodindinca.com/" },
  });

  useEffect(() => {
    axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/visites`).then((r) => setVisites(r.data)).catch(() => {});
    const ws = new WebSocket(`${process.env.REACT_APP_BACKEND_URL.replace(/^http/, "ws")}/api/ws`);
    ws.onmessage = (e) => {
      try {
        const d = JSON.parse(e.data);
        if (d.type === "visites") setVisites(d.visites);
      } catch {}
    };
    return () => ws.close();
  }, []);

  return (
    <div>
      <Navbar />

      {/* HERO */}
      <section ref={heroRef} className="relative min-h-screen flex items-center bg-[#12100E] overflow-hidden pt-16">
        <motion.div style={{ y: yOrb }} className="absolute inset-0 pointer-events-none">
          <div className="hero-orb absolute -top-32 -right-32 w-[560px] h-[560px] rounded-full blur-[140px]" style={{ background: "#E05638", opacity: 0.35 }} />
          <div className="hero-orb absolute -bottom-40 -left-24 w-[480px] h-[480px] rounded-full blur-[140px]" style={{ background: "#0A5C36", opacity: 0.4, animationDelay: "2s" }} />
          <div className="hero-orb absolute top-1/3 left-1/2 w-[300px] h-[300px] rounded-full blur-[120px]" style={{ background: "#D4AF37", opacity: 0.18, animationDelay: "4s" }} />
        </motion.div>

        <motion.div style={{ y: yText }} className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24 w-full">
          {/* Compteur global de visiteurs — en haut à droite du hero */}
          <div className="absolute top-4 right-4 sm:right-6 lg:right-8 z-30">
            <VisitorCounter variante="badge" />
          </div>
          <div className="max-w-3xl">
            <span className="reveal-line mb-6">
              <span style={{ animationDelay: "0.1s" }}>
                <span className="inline-flex items-center gap-2 text-[#D4AF37] text-sm font-semibold tracking-[0.25em] uppercase">
                  <span className="w-8 h-px bg-[#D4AF37]" /> Côte d'Ivoire · 🇨🇮
                </span>
              </span>
            </span>
            <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight leading-[1.05] text-stone-50">
              <span className="reveal-line"><span style={{ animationDelay: "0.25s" }}>Il Faut dindin une GO,</span></span>
              <span className="reveal-line"><span style={{ animationDelay: "0.4s" }}>un Love, une RENCONTRE</span></span>
              <span className="reveal-line">
                <span style={{ animationDelay: "0.55s" }}>
                  en Côte d'Ivoire c'est{" "}
                  <span className="bg-gradient-to-r from-[#E05638] via-[#D4AF37] to-[#E05638] bg-clip-text text-transparent">CHAP CHAP&nbsp;!</span>
                </span>
              </span>
            </h1>
            <motion.p
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.85, duration: 0.7 }}
              className="mt-8 text-base md:text-lg text-stone-400 leading-relaxed max-w-xl"
            >
              Trouvez l'amour, l'amitié ou l'exclusivité sur la plateforme conçue spécialement pour la communauté ivoirienne. Trois expériences, un seul cœur.
            </motion.p>
            <motion.div
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 1.05, duration: 0.7 }}
              className="mt-10 flex flex-wrap gap-4"
            >
              <a
                href="#categories"
                data-testid="hero-explore-btn"
                className="inline-flex items-center gap-2 bg-[#E05638] hover:bg-[#C84428] text-white font-semibold px-7 py-3.5 rounded-full transition-colors duration-200"
              >
                Explorer les catégories <ArrowRight size={18} />
              </a>
              <Link
                to="/inscription"
                data-testid="register-button-hero"
                className="inline-flex items-center gap-2 border border-white/20 bg-white/[0.04] backdrop-blur-md hover:bg-white/[0.1] text-stone-100 font-semibold px-7 py-3.5 rounded-full transition-colors duration-200"
              >
                Créer mon profil gratuit
              </Link>
            </motion.div>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 1.3, duration: 0.8 }}
              className="mt-16 grid grid-cols-3 gap-6 max-w-lg"
            >
              {[
                ["4", "Expériences de rencontre"],
                ["4+", "Villes couvertes"],
                ["100%", "Ivoirien & fier"],
              ].map(([num, label]) => (
                <div key={label}>
                  <div className="font-heading text-3xl font-extrabold text-[#D4AF37]">{num}</div>
                  <div className="text-xs text-stone-500 mt-1 leading-snug">{label}</div>
                </div>
              ))}
            </motion.div>
          </div>
        </motion.div>
      </section>

      <Marquee />

      <OffreDecouverteBanner />

      {/* CATEGORIES */}
      <section id="categories" className="py-24 bg-[#FAF8F5]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="max-w-2xl mb-16"
          >
            <span className="text-[#E05638] text-sm font-semibold tracking-[0.25em] uppercase">Quatre univers</span>
            <h2 className="font-heading text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight text-stone-900 mt-3">
              Choisissez votre expérience
            </h2>
            <p className="text-stone-600 mt-4 leading-relaxed">
              Du maquis convivial au salon feutré — et jusqu'à votre escale en Europe —
              FodindinCa a pensé une catégorie pour chaque cœur ivoirien.
            </p>
          </motion.div>
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-8 items-stretch">
            {Object.values(CATEGORIES).map((cat, i) => (
              <CategoryCard key={cat.id} cat={cat} index={i} />
            ))}
            <CarteEurope index={3} />
          </div>
          <div className="grid grid-cols-3 gap-4 mt-10" data-testid="compteur-visiteurs">
            {Object.values(CATEGORIES).map((cat) => (
              <div
                key={cat.id}
                data-testid={`visiteurs-${cat.id}`}
                className="bg-white border border-[#E5DFD5] rounded-2xl px-4 py-4 text-center"
              >
                <div className="font-heading text-2xl font-extrabold" style={{ color: cat.color }}>
                  {visites[cat.id] ?? 0}
                </div>
                <div className="text-xs text-stone-500 mt-1">
                  visiteur{(visites[cat.id] ?? 0) > 1 ? "s" : ""} · {cat.name}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* À LA UNE : dernières annonces ClubVIP et Air-Bengue */}
      <AnnoncesALaUne />

      {/* MESSAGING TEASER */}
      <section className="py-16 bg-[#FAF8F5]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="relative bg-[#12100E] rounded-3xl px-8 py-12 md:px-14 md:py-16 overflow-hidden"
            data-testid="messaging-teaser-banner"
          >
            <div className="absolute -right-20 -top-20 w-72 h-72 rounded-full blur-[100px]" style={{ background: "#D4AF37", opacity: 0.25 }} />
            <div className="relative z-10 flex flex-col md:flex-row md:items-center gap-8 justify-between">
              <div className="max-w-xl">
                <span className="inline-block text-[11px] font-bold tracking-[0.2em] uppercase text-[#12100E] bg-[#D4AF37] px-3 py-1.5 rounded-full mb-5">
                  Prochainement
                </span>
                <h3 className="font-heading text-2xl md:text-3xl font-extrabold text-stone-50">
                  Messagerie instantanée & photos de profil
                </h3>
                <p className="text-stone-400 mt-3 leading-relaxed text-sm md:text-base">
                  La Phase 2 arrive bientôt : discutez en direct, partagez vos photos et flashez sans limite.
                </p>
              </div>
              <div className="shrink-0">
                <MessageCircle size={72} strokeWidth={1} className="text-[#D4AF37]/60" />
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-24 bg-[#FAF8F5]">
        <div className="max-w-3xl mx-auto px-4 text-center">
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <h2 className="font-heading text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight text-stone-900">
              Votre histoire commence <span className="text-[#E05638]">aujourd'hui</span>
            </h2>
            <p className="text-stone-600 mt-4 leading-relaxed">
              Inscription simple, sans mot de passe. Créez votre profil en moins d'une minute.
            </p>
            <Link
              to="/inscription"
              data-testid="cta-register-btn"
              className="mt-8 inline-flex items-center gap-2 bg-[#0A5C36] hover:bg-[#074528] text-white font-semibold px-8 py-4 rounded-full transition-colors duration-200"
            >
              Rejoindre FodindinCa <ArrowRight size={18} />
            </Link>
            <div className="mt-8 flex justify-center">
              <VisitorCounter variante="minimal" suivre={false} clair />
            </div>
          </motion.div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
